import { skipToken } from "@reduxjs/toolkit/query";

import { useAppSelector } from "app/redux-store-interactions";
import type { AppDispatch } from "app/store";
import type { FetchMiniMapDataArgs } from "features/miniMap/fetchMiniMapData";
import { useSceneId } from "hooks/useSceneId";
import { selectBackendProvider, selectProcoreContext } from "slices/explorer";

import { procoreApi, useGetModelRevisionsQuery } from "./procoreApi";
import type { ModelRevision } from "./types";

export interface ModelRevisionsArgs {
    companyId: number;
    projectId: number;
    sceneId: string;
    sceneVersionId: string;
}

/**
 * Canonical args for the `getModelRevisions` endpoint. All consumers build args
 * here so they share one cache key (and one request) per opened model. Returns
 * `null` when the context is insufficient.
 */
export function buildModelRevisionsArgs(
    procoreContext: { companyId: number; projectId: number; sceneVersionId?: string } | null | undefined,
    sceneId: string | undefined,
): ModelRevisionsArgs | null {
    if (!procoreContext || !procoreContext.companyId || !procoreContext.projectId || !sceneId) {
        return null;
    }

    return {
        companyId: procoreContext.companyId,
        projectId: procoreContext.projectId,
        sceneId,
        // Fall back to sceneId so version-less scenes still resolve.
        sceneVersionId: procoreContext.sceneVersionId ?? sceneId,
    };
}

/** Picks the revision for the opened scene: scene-version match, then scene-id match. */
export function selectSceneRevision(
    revisions: ModelRevision[] | undefined,
    { sceneVersionId, sceneId }: { sceneVersionId?: string; sceneId: string },
): ModelRevision | undefined {
    if (!revisions?.length) {
        return undefined;
    }

    return (
        revisions.find((r) => r.current_platform_scene_version_id === sceneVersionId) ??
        revisions.find((r) => r.current_platform_scene_id === sceneId)
    );
}

/**
 * Canonical args for the `getMiniMapData` endpoint, for the opened scene's
 * model. The mini map slice mirrors every `getMiniMapData` result, so the
 * widget, the offline download and the prune all build args here: a caller on
 * another model or slice would replace the sheets on screen. Returns
 * `undefined` until the opened scene's revision, and so its model, is known.
 */
export function buildMiniMapDataArgs(
    procoreContext: { companyId: number; projectId: number; sceneVersionId?: string; sceneSliceId?: string },
    revisions: ModelRevision[] | undefined,
    sceneId: string,
): FetchMiniMapDataArgs | undefined {
    const revision = selectSceneRevision(revisions, { sceneVersionId: procoreContext.sceneVersionId, sceneId });
    if (!revision?.bim_model_id) {
        return undefined;
    }

    return {
        companyId: procoreContext.companyId,
        projectId: procoreContext.projectId,
        modelId: revision.bim_model_id,
        // Narrows to the open Snapshot's sheets; absent for the live
        // federated model (BM-14175).
        sliceId: procoreContext.sceneSliceId,
    };
}

/**
 * Subscribes to the model revisions for the opened scene via the shared,
 * session-cached query. Production only fetches under the Procore backend;
 * dev (`import.meta.env.DEV`, tree-shaken out of prod) fetches regardless so
 * local mock data works. Never fetches in standalone (no Procore context).
 */
export function useModelRevisions() {
    const backendProvider = useAppSelector(selectBackendProvider);
    const procoreContext = useAppSelector(selectProcoreContext);
    const sceneId = useSceneId();

    const enabled = backendProvider === "procore" || import.meta.env.DEV;
    const args = enabled ? buildModelRevisionsArgs(procoreContext, sceneId) : null;
    const query = useGetModelRevisionsQuery(args ?? skipToken);

    return { ...query, sceneId, sceneVersionId: procoreContext?.sceneVersionId };
}

/** Imperative fetch for non-React callers; reuses the shared session cache. */
export async function fetchModelRevisions(dispatch: AppDispatch, args: ModelRevisionsArgs): Promise<ModelRevision[]> {
    return dispatch(procoreApi.endpoints.getModelRevisions.initiate(args)).unwrap();
}
